import pick from 'lodash/pick';
import { getStateFromReport, getStateFromReportSet } from '../customReports/customReportsMenu.initialState';
import { CustomReportTypes, defaultServerSideParams } from '../../shared/customReports/constants';
import {
  BatchReportParameters,
  BatchReportItemWithReportDetails,
  ReportParameters,
  StandardReportDefinition,
  isCustomReport,
  isStandardReport,
  CustomReportDefinition,
} from '../../shared/types/batchReports';
import { isReportSet, isSingleReport, ParamValues } from '../../shared/types/customReports';
import { getParamNamesForQuery, getParamsForEachReport, getColumnsHeaderName } from '../../shared/customReports/helpers';
import { dateOptions, generateExportFileName, timeOptions } from '@common/utils/generateExportFileName';
import { LocalStorageBatchReportParameters } from './localStorage';
import { evaluateExpression } from '@common/utils/evaluateExpression';
import {
  ReportSetType,
  SingleReportType,
  StandardReportType,
  FormattingOptions,
  AllParamOptions,
  ParamOptions,
  BatchReportQuery,
  ExportPayload,
  ExportPayloadParams,
  ArchivalExportPayload,
  ArchivalExportPayloadParams,
} from './types';

const PAYLOAD_LIMIT = 85000;

export const getParams = (reportDefinition: CustomReportDefinition) => {
  if (isReportSet(reportDefinition)) {
    return getStateFromReportSet(reportDefinition.reports);
  }

  if (reportDefinition.customReportType === CustomReportTypes.REPORT) {
    return getStateFromReport(reportDefinition);
  }

  return {};
};

export const formatDateTimeForBatchReportJobName = (date: Date) => {
  const datePart = new Intl.DateTimeFormat('default', dateOptions)
    .format(date);

  const timePart = new Intl.DateTimeFormat('default', timeOptions)
    .format(date);

  return `${datePart} ${timePart}`;
};

const getParamsForStandardReport = (
  reportDefinition: StandardReportDefinition,
  defaultReportValues: any
) =>
  Object.keys(reportDefinition.params).reduce((params, name) => {
    const emptyValue = reportDefinition.params[name].type === 'multiselect' ? [] : undefined; // eslint-disable-line no-undefined
    const defaultValue = (defaultReportValues[reportDefinition.reportName] ?? {})[name];
    params[name] = defaultValue || emptyValue;
    return params;
  }, {} as Record<string, string | string[]>);

const applyCurrentPeriodIfNecessary
  = (queryParams: Record<string, any>, defaultValue: any) =>
    Object.keys(queryParams).reduce((params, param) => {
      params[param] = defaultValue[param] ?? queryParams[param];
      return params;
    }, {} as Record<string, any>);

export const generateBatchReportParameters = (
  reports: BatchReportItemWithReportDetails[],
  defaultReportValues: any
) => reports.reduce(
  (acc: BatchReportParameters, report: BatchReportItemWithReportDetails) => {
    const defaultValue = defaultReportValues[report.reportDefinition?.reportSetId] ?? {};
    const queryParams = report.itemType === 'CR'
      ? applyCurrentPeriodIfNecessary(getParams(report.reportDefinition as CustomReportDefinition).queryParams,
        defaultValue)
      : getParamsForStandardReport(report.reportDefinition as any, defaultReportValues);

    return {
      ...acc,
      [report.itemName]: {
        reportParamValues: queryParams,
        isIncludedInExport: true,
        reportDefinition: report.reportDefinition,
      },
    };
  }, {});

export type MergeBatchReportParametersOptions = {
  useDefaults?: boolean,
};

export const getMergedBatchReportParameters = (
  initialBatchReportParameters: BatchReportParameters,
  localStorageBatchReportParameters: LocalStorageBatchReportParameters,
  { useDefaults = false }: MergeBatchReportParametersOptions = {}
) => Object.keys(initialBatchReportParameters).reduce((acc, reportName) => {
  const reportParamValues = initialBatchReportParameters[reportName]?.reportParamValues || {};
  const cachedReportParamValues = localStorageBatchReportParameters[reportName]?.reportParamValues || {};
  const mergedParamValues = Object.keys(reportParamValues).reduce((paramAcc, paramName) => {
    const initial = reportParamValues[paramName];
    const cached = cachedReportParamValues[paramName];
    let merged;
    if (useDefaults) {
      merged = initial ? initial : cached;
    } else {
      merged = cached ? cached : initial;
    }
    return { ...paramAcc, [paramName]: merged };
  }, {});

  const mergedIsIncludedInExport = typeof localStorageBatchReportParameters[reportName]?.isIncludedInExport === 'undefined'
    ? initialBatchReportParameters[reportName]?.isIncludedInExport
    : localStorageBatchReportParameters[reportName]?.isIncludedInExport;

  const mergedParameters = {
    ...initialBatchReportParameters[reportName],
    reportParamValues: mergedParamValues,
    isIncludedInExport: mergedIsIncludedInExport,
  };

  return {
    ...acc,
    [reportName]: mergedParameters,
  };
}, {});

const getParamNamesToInclude = (reportDefinition: StandardReportDefinition, reportParamValues: ParamValues) =>
  Object.keys(reportDefinition.params).filter(paramName => {
    const hideWhen = reportDefinition.params[paramName].hideWhen;
    if (hideWhen) {
      return !evaluateExpression(hideWhen, reportParamValues);
    }
    return true;
  });

const filterParamOptions = (paramOptions: ParamOptions, reportParamValues: ParamValues) =>
  Object.entries(paramOptions)
    .reduce((obj, [key, options]) => ({
      ...obj,
      [key]: Array.isArray(reportParamValues[key])
        ? options.filter(({ value }) => reportParamValues[key]?.includes(value))
        : options.filter(({ value }) => value === reportParamValues[key]),
    }), {});

export const getReportsForExport = (
  batchReportParameters: BatchReportParameters,
  formattingOptions: FormattingOptions = {},
  paramOptions: AllParamOptions = {}
) => {
  const batchReportsIncludedInExportKeys = Object.keys(batchReportParameters)
    .filter(key => batchReportParameters[key].isIncludedInExport);
  const exportedBatchReports = pick(batchReportParameters, batchReportsIncludedInExportKeys);
  return Object.values(exportedBatchReports).map((reportParameters: ReportParameters) => {
    const reportDefinition = reportParameters?.reportDefinition;
    if (isCustomReport(reportDefinition) && isReportSet(reportDefinition)) {
      const { mapping } = getParams(reportDefinition);
      const queries: BatchReportQuery[] = getParamsForEachReport({
        reports: reportDefinition.reports,
        queryParams: reportParameters.reportParamValues,
        mapping,
      });
      return {
        type: CustomReportTypes.REPORT_SET,
        data: {
          reportId: reportDefinition.reportSetId,
          name: reportDefinition.reportSetName,
          queries: queries.map(query => ({
            reportId: query.reportId,
            name: query.pillName,
            params: query.params,
            reportOptions: query.reportOptions,
          })),
        },
      } as ReportSetType;
    }
    if (isCustomReport(reportDefinition) && isSingleReport(reportDefinition)) {
      const paramNames = getParamNamesForQuery(reportDefinition.sql);
      const params = {
        ...(reportDefinition.serverSide ? defaultServerSideParams : {}),
        ...pick(reportParameters.reportParamValues, paramNames),
      };
      return {
        type: CustomReportTypes.REPORT,
        data: {
          reportId: reportDefinition.reportSetId,
          name: reportDefinition.reportSetName,
          params,
          reportOptions: {
            columnDefinitions: getColumnsHeaderName(reportDefinition.columns),
            filters: {},
          },
        },
      } as SingleReportType;
    }
    if (isStandardReport(reportDefinition)) {
      const paramNamesToInclude = getParamNamesToInclude(reportDefinition, reportParameters.reportParamValues);
      return {
        type: 'StandardReport',
        data: {
          name: reportDefinition.reportName,
          label: reportDefinition.reportLabel,
          params: pick(reportParameters.reportParamValues, paramNamesToInclude),
          formattingOptions,
          paramOptions: filterParamOptions(
            pick(paramOptions[reportDefinition.reportName], paramNamesToInclude),
            reportParameters.reportParamValues
          ) ?? {},
        },
      } as StandardReportType;
    }
    return {};
  });
};

const areAllValuesSelectedForReport = (reportParamValues: ParamValues, archivalFlag: boolean) => {
  if (archivalFlag) {
    const entityKey = Object.keys(reportParamValues).filter(item => item.includes('entity'))[0];
    const companyCodeKey = Object.keys(reportParamValues).filter(item => item.includes('companyCode'))[0];

    if (entityKey) {
      reportParamValues[entityKey] = 'TBD';
    }

    if (companyCodeKey) {
      reportParamValues[companyCodeKey] = 'TBD';
    }
  }
  if (reportParamValues) {
    const nonValidatorParams = Object.entries(reportParamValues)
      .filter(([key]) => !key.includes('validator_'));

    return nonValidatorParams.every(([, value]) => value);
  }

  return true;
};

export const areAllValuesSelectedForBatchReport
  = (batchReportParameters: BatchReportParameters, archivalFlag: boolean): boolean => {
    if (batchReportParameters) {
      const batchReportsIncludedInExportKeys = Object.keys(batchReportParameters)
        .filter(key => batchReportParameters[key].isIncludedInExport);
      const exportedBatchReports = pick(batchReportParameters, batchReportsIncludedInExportKeys);
      return Object.values(exportedBatchReports)
        .every(reportParameters => {
          if (isStandardReport(reportParameters.reportDefinition)) {
            return areAllValuesSelectedForReport(
              pick(
                reportParameters.reportParamValues,
                getParamNamesToInclude(reportParameters.reportDefinition, reportParameters.reportParamValues)
              ), archivalFlag
            );
          }
          return areAllValuesSelectedForReport(reportParameters.reportParamValues, archivalFlag);
        });
    }
    return false;
  };

export const generateExportPayload = ({
  batchId,
  batchName,
  environmentSuffix,
  reports,
  sftpDeliveryFlag,
  exportFormat,
}: ExportPayload): ExportPayloadParams => {
  const filename = generateExportFileName({
    reportName: batchName,
    environmentSuffix,
    includeDateTime: true,
  });
  return {
    batchId,
    name: filename,
    displayName: `Batch reports (${batchName}) ${formatDateTimeForBatchReportJobName(new Date())}`,
    reports,
    sftpDeliveryFlag: sftpDeliveryFlag ? 'Y' : 'N',
    exportFormat,
  };
};

export const generateArchivalPayload = ({
  batchId,
  batchName,
  reports,
  entity,
  reportName,
  sftpDeliveryFlag,
  exportFormat,
}: ArchivalExportPayload): ArchivalExportPayloadParams => {
  const cCodeName = entity ? entity.label : '';
  return {
    batchId,
    entityName: entity.label,
    entityValue: entity.value,
    reportName,
    displayName: `${batchName} ${cCodeName} ${formatDateTimeForBatchReportJobName(new Date())}`,
    reports,
    sftpDeliveryFlag: sftpDeliveryFlag ? 'Y' : 'N',
    exportFormat,
  };
};

export const isValidPayloadLimit = (payload: ExportPayloadParams | ArchivalExportPayloadParams | null) => {
  const payloadSize = JSON.stringify(payload || {}).length;
  return payloadSize > PAYLOAD_LIMIT;
};
